import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import {
    KeyRound, Mail, ShieldAlert, Building2, Loader2, ScrollText, Smartphone,
} from 'lucide-react';
import useAxiosAuth from '../../hooks/useAxiosAuth';
import baseURL from '../../utils/baseURL';

// PASSWORD — for the MITRA ONE CARE credential, and it says so.
//
// The tenant app's Password tab is `patientOnly`: a signed-in patient changing
// their own password on a per-tenant `users` row, proved by typing the current
// one. Neither half of that survives the move here.
//
//   WHICH CREDENTIAL. A MOC identity's password lives on moc_accounts, not on
//   any hospital's users row. Those are genuinely different secrets — a patient
//   can hold a hospital login AND a Mitra One Care login with different
//   passwords — so a tab that silently changed one while the header named the
//   other would be a trap. Hence the panel below spelling out which.
//
//   A RESET NEVER PUTS A PASSWORD IN A MAIL OR ON A SCREEN (owner, 2 Oct
//   2026). This tab used to let the operator TYPE the patient's new password
//   (POST /moc-admin/patients/:id/password). It no longer does: that endpoint
//   now makes the current password stop working and sends the patient a
//   message telling them to set a new one with Forgot password. The operator
//   never chooses, sees or sends a password. The second card keeps the gentler
//   lever — send the patient a reset code without stopping the old password.
//
//   WHAT THE SCREEN PROMISES IS WHAT THE API DOES. In particular it does NOT
//   claim the patient is signed out everywhere: the platform's revocation
//   mechanism (api/services/tokenBlacklist.js) can only revoke a token it is
//   handed, and there is no register of an account's issued tokens to walk. The
//   endpoint answers `sessionsRevoked: false` and this panel says so in as many
//   words, with the lever that does work — deactivate the identity — named next
//   to it. A security screen that overstates what it did is worse than one that
//   does less.
//
// A hospital login is a different secret owned by a different party — reset it
// from that hospital's own admin app.

const MocAccountPasswordTab = ({ account, hospitals = [] }) => {
    const { t } = useTranslation(['doctorpassword']);
    const axiosInstance = useAxiosAuth();
    const [sending, setSending] = useState(false);

    const [saving, setSaving] = useState(false);

    const email = account?.email || '';
    // The other reset channel (owner, 2026-09-02): a desk-created identity may
    // hold only a number, and recovery must still reach them — by SMS then.
    const phone = account?.phone || '';
    const mocAccountId = account?.mocAccountId || '';

    const canSubmit = Boolean(mocAccountId) && !saving;

    // channel: 'email' | 'phone' — the endpoint takes either identifier and
    // sends the code down that channel; the patient finishes in their app.
    const sendReset = async (channel) => {
        const target = channel === 'phone' ? phone : email;
        if (!target) return;
        const { isConfirmed } = await Swal.fire({
            title: t('moc.send_reset_title', 'Send a password reset code?'),
            text: channel === 'phone'
                ? t('moc.send_reset_text_sms',
                    'A one-time code goes by SMS to {{phone}}. Only the patient can complete the reset — you will not see the new password.',
                    { phone })
                : t('moc.send_reset_text',
                    'A one-time code goes to {{email}}. Only the patient can complete the reset — you will not see the new password.',
                    { email }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#9DB3B1',
            confirmButtonText: t('moc.send_reset_confirm', 'Send code'),
            cancelButtonText: t('doctorpassword.cancel', 'Cancel'),
        });
        if (!isConfirmed) return;

        setSending(true);
        try {
            // The endpoint answers the same way whether or not the account exists —
            // it is deliberately not an enumeration oracle — so a 200 here means
            // "accepted", never "that inbox is real".
            const { data } = await axiosInstance.post(`${baseURL}/moc-auth/forgot-password`,
                channel === 'phone' ? { phone } : { email });
            if (channel === 'phone' && data?.delivery === 'simulated') {
                // Dev stacks have no SMS gateway — say so instead of letting the
                // operator wait for a text that never comes (the code is the
                // fixed dev code).
                toast.info(t('moc.send_reset_simulated', 'No SMS gateway on this environment — the dev code 123456 applies'));
            } else {
                toast.success(t('moc.send_reset_ok', 'Reset code sent to the patient'));
            }
        } catch (error) {
            toast.warn(error?.response?.data?.message || t('moc.send_reset_failed', 'Could not send the reset code'));
        } finally {
            setSending(false);
        }
    };

    const resetPassword = async () => {
        if (!canSubmit) return;

        // `text:`, NEVER `html:`. The message interpolates the PATIENT'S OWN
        // name — self-registered, stored with only String().trim() — and this
        // app's i18next sets escapeValue:false ("React already escapes"), which
        // is true for JSX but not for SweetAlert2's html option (raw innerHTML).
        // With html: a patient registered as <img src=x onerror=...> would run
        // script in the Super Admin's browser the moment an operator opened this
        // dialog. The sendReset dialog above uses text: for the same reason.
        const { isConfirmed } = await Swal.fire({
            title: t('moc.reset_password_confirm_title'),
            text: t('moc.reset_password_confirm_text',
                { name: [account?.name, account?.surname].filter(Boolean).join(' ') || t('appointmentmodal.patient_fallback', 'Patient') }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#9DB3B1',
            confirmButtonText: t('passwordReset.confirm'),
            cancelButtonText: t('passwordReset.cancel'),
        });
        if (!isConfirmed) return;

        setSaving(true);
        try {
            // Nothing in the body: the operator never chooses the password.
            const { data } = await axiosInstance.post(`${baseURL}/moc-admin/patients/${mocAccountId}/password`, {});
            toast.success(data?.data?.noticeDelivered === false
                ? t('passwordReset.done_not_sent')
                : t('passwordReset.done_sent'));
        } catch (error) {
            toast.warn(error?.response?.data?.message || t('passwordReset.error'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="moc-password-tab" className="mt-4">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                    {t('doctorpassword.heading', 'Password')}
                </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                {/* RESET THE PASSWORD — the old one stops working, the patient
                    sets theirs with Forgot password. Nothing to type. */}
                <div className="rounded-2xl border border-[#D7A05C]/40 bg-white p-5">
                    <div className="flex items-center gap-2 mb-3">
                        <div className="p-2 rounded-lg bg-gradient-to-br from-[#4ABDBD] to-[#2D8A8A]">
                            <KeyRound className="w-4 h-4 text-white" />
                        </div>
                        <h3 className="text-sm font-bold text-[#1a365d]">
                            {t('moc.reset_password_heading')}
                        </h3>
                    </div>
                    <p className="text-xs leading-relaxed text-gray-600">
                        {t('moc.reset_password_note')}
                    </p>

                    {/* The two consequences an operator is most likely to guess
                        wrongly. Both are facts about the API, not warnings for
                        their own sake — see the file header. */}
                    <div className="mt-4 space-y-2">
                        <p className="flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2 text-[11px] leading-relaxed text-amber-800">
                            <Smartphone className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                            <span>{t('moc.sessions_not_revoked')}</span>
                        </p>
                        <p className="flex items-start gap-2 rounded-xl bg-gray-50 px-3 py-2 text-[11px] leading-relaxed text-gray-600">
                            <ScrollText className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-400" />
                            <span>{t('moc.set_password_is_logged')}</span>
                        </p>
                    </div>

                    <button
                        type="button"
                        data-testid="moc-reset-password-submit"
                        onClick={resetPassword}
                        disabled={!canSubmit}
                        className={`mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm text-white shadow-lg transition-all duration-300 ${!canSubmit
                            ? 'bg-gray-300 cursor-not-allowed'
                            : 'bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] hover:shadow-xl hover:scale-105 cursor-pointer'}`}
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                        {t('passwordReset.confirm')}
                    </button>
                </div>

                <div className="space-y-6">
                    {/* SEND A RESET CODE — kept, and still the recommended action. */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-5">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="p-2 rounded-lg bg-[#4ABDBD]/15">
                                <Mail className="w-4 h-4 text-[#2D8A8A]" />
                            </div>
                            <h3 className="text-sm font-bold text-[#1a365d]">
                                {t('moc.which_credential', 'Which password this is')}
                            </h3>
                        </div>
                        <p className="text-xs leading-relaxed text-gray-600">
                            {t('moc.which_credential_note',
                                'The Mitra One Care sign-in — the one account this person uses across every connected hospital. It is stored on the global identity, not on any hospital’s own records.')}
                        </p>
                        <div className="mt-3 space-y-1.5">
                            <p className="inline-flex items-center gap-2 rounded-xl bg-gray-50 px-3 py-2 text-xs text-gray-700">
                                <Mail className="w-3.5 h-3.5 text-gray-400" />
                                <span className="font-mono break-all">{email || t('moc.no_email', 'No email on this identity')}</span>
                            </p>
                            <p className="inline-flex items-center gap-2 rounded-xl bg-gray-50 px-3 py-2 text-xs text-gray-700 ml-0 sm:ml-1.5">
                                <Smartphone className="w-3.5 h-3.5 text-gray-400" />
                                <span className="font-mono break-all">{phone || t('moc.no_phone', 'No mobile number on this identity')}</span>
                            </p>
                        </div>
                        <p className="mt-3 text-[11px] leading-relaxed text-gray-500">
                            {t('moc.send_reset_note',
                                'The safer of the two: the patient proves they hold the inbox and chooses the password themselves, so you never learn it and nothing changes until they act.')}
                        </p>

                        <button
                            type="button"
                            data-testid="moc-send-password-reset"
                            onClick={() => sendReset('email')}
                            disabled={!email || sending}
                            className={`mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm shadow-sm transition-all duration-300 border ${!email || sending
                                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                : 'bg-white text-[#2D8A8A] border-[#4ABDBD] hover:bg-[#4ABDBD]/10 hover:shadow-md cursor-pointer'}`}
                        >
                            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                            {t('moc.send_reset_button', 'Send password reset code')}
                        </button>
                        {!email && (
                            <p className="mt-2 text-[11px] text-amber-600" data-testid="moc-reset-no-email-reason">
                                {t('moc.reset_needs_email', 'The patient has not given an email address, so no email code can be sent — use the SMS code below.')}
                            </p>
                        )}

                        <button
                            type="button"
                            data-testid="moc-send-password-reset-sms"
                            onClick={() => sendReset('phone')}
                            disabled={!phone || sending}
                            className={`mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm shadow-sm transition-all duration-300 border ${!phone || sending
                                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                : 'bg-white text-[#2D8A8A] border-[#4ABDBD] hover:bg-[#4ABDBD]/10 hover:shadow-md cursor-pointer'}`}
                        >
                            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Smartphone className="w-4 h-4" />}
                            {t('moc.send_reset_button_sms', 'Send reset code by SMS')}
                        </button>
                        {!phone && (
                            <p className="mt-2 text-[11px] text-amber-600" data-testid="moc-reset-no-phone-reason">
                                {t('moc.reset_needs_phone', 'The patient has not given a mobile number, so no SMS code can be sent.')}
                            </p>
                        )}
                    </div>

                    <div className="rounded-2xl border border-gray-200 bg-white p-5">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="p-2 rounded-lg bg-[#D7A05C]/15">
                                <ShieldAlert className="w-4 h-4 text-[#8a5f26]" />
                            </div>
                            <h3 className="text-sm font-bold text-[#1a365d]">
                                {t('moc.not_this_credential', 'What this does NOT change')}
                            </h3>
                        </div>
                        <p className="text-xs leading-relaxed text-gray-600">
                            {t('moc.not_this_credential_note',
                                'A hospital’s own desk login for a patient, where one exists, is a separate secret owned by that hospital — reset it from their admin app, not from here. Changing this password does not touch it.')}
                        </p>
                        {hospitals.length > 0 && (
                            <ul className="mt-3 space-y-1.5">
                                {hospitals.map((h) => (
                                    <li key={h.tenantId} className="flex items-center gap-2 rounded-xl bg-gray-50 px-3 py-2">
                                        <Building2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                        <span className="min-w-0 flex-1 truncate text-xs text-gray-700">{h.hospitalName || t('moc.hr_detail_hospital', 'Hospital')}</span>
                                        {/* The hospital's own patient number, when
                                            the API sends it — not the person id,
                                            which is one string for every hospital. */}
                                        <span className="font-mono text-[10px] text-gray-400">
                                            {h.hospitalPatientCode || h.tenantPatientCode || ''}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MocAccountPasswordTab;
